import React, { useCallback, useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';

import { loadStoreOffices, setContentOffice } from './api';
import {
  Empty, Failed, Head, Loading, XomicsSelect,
} from './parts';
import { tx } from './i18n';
import { useLiveResource } from './useLive';
import './storeOffice.css';

// The office whose store a page is changing, and the line that says so.
//
// The catalogue is one, but what is sold, at what price, which bundles are
// offered and which menu rows show is each office's own, and a visitor gets
// the store of the office responsible for their country (Locations). So every
// page that changes a store names the office at its top, with the countries
// that see it. An account works on its own office's store; one that manages
// every office's chooses.
const CHOICE_KEY = 'xo-store-office';

// An office somebody visits: it serves a country of the storefront, or every
// country no office holds.
const isLive = (office) => office.markets.length > 0 || office.isDefault;

// The office a page opens on: the one chosen last, else the account's own when
// somebody visits its store, else the website default (the store most
// visitors see), else its own.
function startingOffice(offices, ownOfficeId, chosenId) {
  const byId = (id) => offices.find((office) => office.id === id) ?? null;
  const own = byId(ownOfficeId);
  return byId(chosenId) ?? (own && isLive(own) ? own : null)
    ?? offices.find((office) => office.isDefault) ?? own ?? offices[0] ?? null;
}

function savedChoice() {
  try {
    const id = Number(window.localStorage.getItem(CHOICE_KEY));
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  } catch (_error) {
    return null;
  }
}

export function useStoreOffice() {
  const resource = useLiveResource(['locations', 'products'], useCallback(() => loadStoreOffices(), []));
  const [chosenId, setChosenId] = useState(savedChoice);
  const offices = useMemo(() => resource.data?.offices ?? [], [resource.data]);
  const office = useMemo(
    () => startingOffice(offices, resource.data?.ownOfficeId ?? null, chosenId),
    [chosenId, offices, resource.data],
  );
  const choose = useCallback((id) => {
    setChosenId(id);
    try {
      window.localStorage.setItem(CHOICE_KEY, String(id));
    } catch (_error) {
      // The choice still holds for this visit when storage is unavailable.
    }
  }, []);
  return {
    loading: resource.loading && resource.data === null,
    error: resource.error,
    refresh: resource.refresh,
    offices,
    office,
    canChooseAny: resource.data?.canChooseAny === true,
    choose,
  };
}

function countryNames() {
  try {
    const names = new Intl.DisplayNames(undefined, { type: 'region' });
    return (country) => names.of(country) || country;
  } catch (_error) {
    return (country) => country;
  }
}

// What an office is called: its company where it has one written down (two
// offices can share a name), else its name. Never its code, which is internal.
export const officeTitle = (office) => office.legalName || office.name;

// A few countries are simply named; more than this many are counted, with
// their names a pointer away.
const COUNTRIES_NAMED = 3;

// Whose visitors get an office's store: its countries by name. Many are said
// as a number that opens the whole list under the pointer, or under the
// keyboard's focus.
function OfficeReach({ office }) {
  const nameOf = useMemo(countryNames, []);
  const names = useMemo(
    () => office.markets.map((market) => nameOf(market.countryCode)).sort((left, right) => left.localeCompare(right)),
    [nameOf, office],
  );
  if (names.length === 0) return <span>{tx('ui.store.no_countries')}</span>;
  if (names.length <= COUNTRIES_NAMED) return <span>{names.join(', ')}</span>;
  return (
    <span className="xo-whose-reach" tabIndex={0}>
      <u>{tx('ui.store.countries', { count: names.length })}</u>
      <span className="xo-whose-countries" role="tooltip">
        {names.map((name) => <span key={name}>{name}</span>)}
      </span>
    </span>
  );
}

// Where an office is, on one line, in the order the location manager keeps
// it: street, postcode, town, region, country.
function officeAddress(office, nameOf) {
  const { line1, line2, postalCode, city, region } = office.address ?? {};
  const parts = [line1, line2, postalCode, city, region].filter((part) => typeof part === 'string' && part.trim() !== '');
  return parts.length === 0 ? '' : [...parts, office.countryCode ? nameOf(office.countryCode) : null].filter(Boolean).join(', ');
}

// An office named in the head of a form or a sheet: what it is called, and
// where it is on the line under that. Never the name alone: two offices can
// share one.
export function OfficeNamed({ office }) {
  const nameOf = useMemo(countryNames, []);
  const address = officeAddress(office, nameOf);
  return (
    <>
      <p>{officeTitle(office)}</p>
      {address !== '' && <address className="xo-office-address">{address}</address>}
    </>
  );
}

// Whose a page is, said right under its title. An office's store: the office
// by its name, with whose visitors get it, and where the office is under
// that. An account that manages every office's store chooses the office there.
export function StoreOfficeLine({ store }) {
  const { office, offices, canChooseAny, choose } = store;
  const nameOf = useMemo(countryNames, []);
  if (office === null) return null;
  const address = officeAddress(office, nameOf);
  return (
    <div className="xo-whose">
      <Building2 aria-hidden="true" />
      {canChooseAny && offices.length > 1 ? (
        <XomicsSelect className="xo-whose-choice" aria-label={tx('ui.store.of')} value={String(office.id)}
          onChange={(event) => choose(Number(event.target.value))}>
          {offices.map((entry) => <option key={entry.id} value={String(entry.id)}>{officeTitle(entry)}</option>)}
        </XomicsSelect>
      ) : <strong>{officeTitle(office)}</strong>}
      <OfficeReach office={office} />
      {address !== '' && <address className="xo-whose-address">{address}</address>}
    </div>
  );
}

// A page of what the store says (the menu, the Longevity Hub, the quiz, the
// help centre). They are written once and shared by the offices; what an
// office's store shows of them is its own. So the page is drawn for one office
// (`children` is given its store, for the line under the page's title) and
// drawn again from the start when another office is chosen: nothing of the
// last office stays on it. Every request the page makes says that office.
export function ForStoreOffice({ title, className = 'xo-platform-page', children }) {
  const store = useStoreOffice();
  const officeId = store.office?.id ?? null;
  // Said before the page is drawn: its first request is made as it appears.
  setContentOffice(officeId);
  if (store.error) return <Failed message={store.error} onRetry={store.refresh} />;
  if (store.loading) return <Loading />;
  // An account whose location keeps no store has nothing of a store to change.
  if (officeId === null) return <div className={className}><Head title={title} /><Empty title={tx('ui.store.no_office')} /></div>;
  return <React.Fragment key={officeId}>{children(store)}</React.Fragment>;
}
